/**
 * Apply watermark to an image before download for free users
 */
export const applyWatermarkToImage = async (
  imageUrlOrBlob: string | Blob,
  watermarkText: string = 'GENOVA AI STUDIO'
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Create canvas with image dimensions
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Configure watermark text style - subtle repeating pattern
      const fontSize = Math.max(14, Math.floor(img.width / 35));
      ctx.font = `500 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.06)';
      ctx.textAlign = 'center';
      
      // Calculate diagonal pattern
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(-45 * Math.PI / 180);
      
      // Draw repeating watermark pattern with optimal spacing
      const spacing = fontSize * 7;
      const rows = Math.ceil(Math.sqrt(canvas.width ** 2 + canvas.height ** 2) / spacing) + 1;
      
      for (let i = -rows; i <= rows; i++) {
        for (let j = -rows; j <= rows; j++) {
          ctx.fillText(
            watermarkText,
            j * spacing * 3,
            i * spacing
          );
        }
      }
      
      ctx.restore();
      
      // Draw modern, premium center watermark badge
      const centerText = watermarkText;
      const centerY = canvas.height / 2;
      const centerX = canvas.width / 2;
      
      // Calculate modern typography
      const mainFontSize = Math.max(18, Math.floor(img.width / 28));
      const subtitleFontSize = Math.max(9, Math.floor(mainFontSize * 0.4));
      const badgePadding = Math.max(20, mainFontSize);
      
      ctx.font = `700 ${mainFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      const textMetrics = ctx.measureText(centerText);
      const textWidth = textMetrics.width;
      
      ctx.font = `500 ${subtitleFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      const subtitleMetrics = ctx.measureText('Upgrade to Pro');
      const subtitleWidth = subtitleMetrics.width;
      
      const maxWidth = Math.max(textWidth, subtitleWidth);
      const badgeWidth = maxWidth + badgePadding * 2;
      const badgeHeight = mainFontSize + subtitleFontSize + badgePadding * 1.8;
      const rectX = centerX - badgeWidth / 2;
      const rectY = centerY - badgeHeight / 2;
      const radius = Math.min(12, mainFontSize * 0.6);
      
      // Modern gradient - vibrant but professional
      const gradient = ctx.createLinearGradient(rectX, rectY, rectX + badgeWidth, rectY + badgeHeight);
      gradient.addColorStop(0, 'rgba(124, 58, 237, 0.95)');  // Vibrant purple
      gradient.addColorStop(1, 'rgba(219, 39, 119, 0.95)');  // Modern pink
      
      // Professional shadow
      ctx.shadowColor = 'rgba(0, 0, 0, 0.25)';
      ctx.shadowBlur = 24;
      ctx.shadowOffsetY = 6;
      
      // Draw rounded rectangle badge
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.moveTo(rectX + radius, rectY);
      ctx.lineTo(rectX + badgeWidth - radius, rectY);
      ctx.quadraticCurveTo(rectX + badgeWidth, rectY, rectX + badgeWidth, rectY + radius);
      ctx.lineTo(rectX + badgeWidth, rectY + badgeHeight - radius);
      ctx.quadraticCurveTo(rectX + badgeWidth, rectY + badgeHeight, rectX + badgeWidth - radius, rectY + badgeHeight);
      ctx.lineTo(rectX + radius, rectY + badgeHeight);
      ctx.quadraticCurveTo(rectX, rectY + badgeHeight, rectX, rectY + badgeHeight - radius);
      ctx.lineTo(rectX, rectY + radius);
      ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
      ctx.closePath();
      ctx.fill();
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      
      // Subtle premium border
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw main brand text
      ctx.font = `700 ${mainFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(centerText, centerX, centerY - subtitleFontSize * 0.6);
      
      // Draw modern subtitle
      ctx.font = `500 ${subtitleFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillText('Upgrade to Pro', centerX, centerY + mainFontSize * 0.5);
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          resolve(url);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      }, 'image/png');
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    // Handle different input types
    if (imageUrlOrBlob instanceof Blob) {
      const url = URL.createObjectURL(imageUrlOrBlob);
      img.src = url;
    } else if (imageUrlOrBlob.startsWith('data:')) {
      img.src = imageUrlOrBlob;
    } else if (imageUrlOrBlob.startsWith('http')) {
      img.src = imageUrlOrBlob;
    } else {
      img.src = imageUrlOrBlob;
    }
  });
};
